import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

const WOLF_EMAILS = [
    'valentino_pintea@hotmail.com',
    'rl2604@yahoo.com',
    'kleinj881@gmail.com',
    'alf9005@hotmail.com',
];

// Gates Signals, Co-movement, Trend Analysis — private to this email only (not the broader Wolf list)
const PRIVATE_EMAILS = [
    'valentino_pintea@hotmail.com',
];

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");

    const API_URL = process.env.REACT_APP_API_URL;

    const verifySubscription = useCallback(async (email) => {
        try {
            const response = await fetch(`${API_URL}/verify-subscriber`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const result = await response.json();
            console.log("API Response:", result);

            if (response.ok && result.isSubscriber) {
                console.log("User is verified.");
                setIsAuthenticated(true);
                localStorage.setItem("lastValidated", new Date().toISOString());
                return true;
            } else {
                console.warn("Access denied. User not found in Substack list.");
                setError("Access denied. Please subscribe on Substack.");
                localStorage.removeItem("token");
                localStorage.removeItem("lastValidated");
                setIsAuthenticated(false);
                return false;
            }
        } catch (error) {
            console.error("Error verifying email:", error);
            setError("Error verifying email. Please try again.");
            return false;
        }
    }, [API_URL]);

    const login = async (email) => {
        setError("");
        const isValid = await verifySubscription(email);
        if (isValid) {
            localStorage.setItem("token", email);
            setIsAuthenticated(true);
        }
        return isValid;
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("lastValidated");
        setIsAuthenticated(false);
        setEmail("");
    };

    useEffect(() => {
        const checkAuthStatus = async () => {
            const storedEmail = localStorage.getItem("token");
            const lastValidated = localStorage.getItem("lastValidated");

            if (storedEmail) {
                const now = new Date();
                const lastCheck = lastValidated ? new Date(lastValidated) : new Date(0);
                const hoursSinceLastCheck = (now - lastCheck) / (1000 * 60 * 60);

                if (hoursSinceLastCheck >= 24) {
                    console.log("Revalidating subscriber status...");
                    await verifySubscription(storedEmail);
                } else {
                    console.log("Using cached authentication.");
                    setIsAuthenticated(true);
                }
            }
            setLoading(false);
        };

        checkAuthStatus();
    }, [verifySubscription]);

    const storedEmail = localStorage.getItem("token");
    const isWolfUser = isAuthenticated && storedEmail &&
        WOLF_EMAILS.includes(storedEmail.toLowerCase());
    const isPrivateUser = isAuthenticated && storedEmail &&
        PRIVATE_EMAILS.includes(storedEmail.toLowerCase());

    const value = {
        isAuthenticated,
        isWolfUser,
        isPrivateUser,
        loading,
        email,
        error,
        setEmail,
        setError,
        login,
        logout,
        verifySubscription
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};